/**
 * ComposePage
 *
 * The main compose UI where users select NFTs to combine.
 * Two-panel layout: NFT selection grid (left) + compose preview (right).
 *
 * Route: /collections/:slug/compose
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { Button } from '../ui/components/Button';
import { Badge } from '../ui/components/Badge';
import { useAuthStore } from '../store/authStore';
import { FeatherArrowLeft, FeatherZap, FeatherExternalLink, FeatherChevronDown, FeatherX } from '@subframe/core';
import {
  getMyComposeNFTs,
  getIncompleteComposeSessions,
  cancelComposeSession,
  validateCompose,
  getComposeStats,
  getComposeHistory,
  type ComposeNFT,
  type ComposeValidation,
  type ComposeStats,
  type ComposeHistoryEntry,
} from '../api/compose';
import { getDrop } from '../api/drops';
import { is3DModelUrl } from '../utils/media';
import { ComposePreviewCanvas } from '../components/compose/ComposePreviewCanvas';
import { ComposeProgressModal } from '../components/compose/ComposeProgressModal';

interface ComposeLaunchData {
  id: string;
  name: string;
  slug: string;
  base_image_url?: string;
  base_image_z_index?: number;
  composition_fee_per_nft_drops?: number;
  compose_enabled?: boolean;
  collection_type?: string;
}

type ComposeSortOption = 'traits-asc' | 'traits-desc' | 'name-asc' | 'name-desc' | 'rarity-asc' | 'rarity-desc';

const composeSortOptions: { value: ComposeSortOption; label: string }[] = [
  { value: 'traits-asc', label: 'Traits: Least to Most' },
  { value: 'traits-desc', label: 'Traits: Most to Least' },
  { value: 'name-asc', label: 'Name: A-Z' },
  { value: 'name-desc', label: 'Name: Z-A' },
  { value: 'rarity-asc', label: 'Rarity: Rare to Common' },
  { value: 'rarity-desc', label: 'Rarity: Common to Rare' },
];

function sortComposeNfts(nfts: ComposeNFT[], sort: ComposeSortOption): ComposeNFT[] {
  return [...nfts].sort((a, b) => {
    switch (sort) {
      case 'traits-asc':
        return a.trait_count - b.trait_count;
      case 'traits-desc':
        return b.trait_count - a.trait_count;
      case 'name-asc':
        return (a.name || '').localeCompare(b.name || '');
      case 'name-desc':
        return (b.name || '').localeCompare(a.name || '');
      case 'rarity-asc':
        // Higher rarity_score = rarer, so descending score = rare first
        return (b.rarity_score ?? 0) - (a.rarity_score ?? 0);
      case 'rarity-desc':
        return (a.rarity_score ?? 0) - (b.rarity_score ?? 0);
      default:
        return 0;
    }
  });
}

export const ComposePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const preselectNftId = searchParams.get('nft');
  const { isAuthenticated, user } = useAuthStore();

  const [launch, setLaunch] = useState<ComposeLaunchData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // User's NFTs
  const [myNfts, setMyNfts] = useState<ComposeNFT[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Validation
  const [validation, setValidation] = useState<ComposeValidation | null>(null);
  const [validating, setValidating] = useState(false);

  // Stats
  const [stats, setStats] = useState<ComposeStats | null>(null);
  const [history, setHistory] = useState<ComposeHistoryEntry[]>([]);

  // Sort
  const [sortOption, setSortOption] = useState<ComposeSortOption>('traits-asc');
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Compose modal
  const [showComposeModal, setShowComposeModal] = useState(false);

  // Incomplete session resume
  const [incompleteSession, setIncompleteSession] = useState<{
    id: string;
    status: string;
    input_nft_token_ids: string[];
    burns_completed: Array<{ nft_token_id: string }>;
    burns_remaining: string[];
  } | null>(null);
  const [cancellingSession, setCancellingSession] = useState(false);

  const handleCancelSession = async () => {
    if (!incompleteSession || !launch?.id || cancellingSession) return;
    if (!window.confirm('Are you sure you want to cancel this build? Any NFTs already burned cannot be recovered.')) return;
    try {
      setCancellingSession(true);
      await cancelComposeSession(launch.id, incompleteSession.id);
      setIncompleteSession(null);
      setSelectedIds(new Set());
      setValidation(null);
    } catch (err) {
      console.error('Failed to cancel session:', err);
      alert(err instanceof Error ? err.message : 'Failed to cancel build');
    } finally {
      setCancellingSession(false);
    }
  };

  // Load launch data
  useEffect(() => {
    if (!slug) return;
    const load = async () => {
      try {
        setLoading(true);
        const dropData = await getDrop(slug);
        const launchData = dropData.drop as ComposeLaunchData;
        setLaunch(launchData);

        // Load stats and history (public, no auth needed)
        const [statsData, historyData] = await Promise.all([
          getComposeStats(launchData.id).catch(() => null),
          getComposeHistory(launchData.id, 1, 10).catch(() => ({ entries: [], total: 0 })),
        ]);
        if (statsData) setStats(statsData);
        setHistory(historyData.entries);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load compose data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  // Load user's NFTs and check for incomplete sessions when authenticated
  useEffect(() => {
    if (!launch?.id || !isAuthenticated) return;
    const load = async () => {
      try {
        const [nfts, incomplete] = await Promise.all([
          getMyComposeNFTs(launch.id),
          getIncompleteComposeSessions(launch.id).catch(() => []),
        ]);
        setMyNfts(nfts);

        // If there's an incomplete session, auto-select its NFTs so the user
        // can see what's in progress and resume via the normal "Start Building" flow.
        if (incomplete.length > 0) {
          const session = incomplete[0];
          setIncompleteSession(session);
          setSelectedIds(new Set(session.input_nft_token_ids));
        } else if (preselectNftId && nfts.some((n) => n.nft_token_id === preselectNftId)) {
          // Pre-select NFT from query param (e.g., navigated from NFT detail Build button)
          setSelectedIds(new Set([preselectNftId]));
        }
      } catch (err) {
        console.error('Failed to load NFTs:', err);
      }
    };
    load();
  }, [launch?.id, isAuthenticated]);

  // Validate selection when it changes
  useEffect(() => {
    if (selectedIds.size < 2 || !launch?.id) {
      setValidation(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setValidating(true);
        const result = await validateCompose(launch.id, Array.from(selectedIds));
        setValidation(result);
      } catch (err) {
        console.error('Validation failed:', err);
      } finally {
        setValidating(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [selectedIds, launch?.id]);

  const toggleNft = useCallback((tokenId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(tokenId)) {
        next.delete(tokenId);
      } else {
        next.add(tokenId);
      }
      return next;
    });
  }, []);

  const handleComposeComplete = useCallback(() => {
    setShowComposeModal(false);
    setSelectedIds(new Set());
    setIncompleteSession(null);
    // Reload NFTs
    if (launch?.id) {
      getMyComposeNFTs(launch.id).then(setMyNfts).catch(console.error);
      getComposeStats(launch.id).then(setStats).catch(console.error);
      getComposeHistory(launch.id, 1, 10).then((r) => setHistory(r.entries)).catch(console.error);
    }
  }, [launch?.id]);

  // Get selected NFTs for preview — preserve selection order for last-wins trait override
  const selectedNfts = Array.from(selectedIds)
    .map((id) => myNfts.find((n) => n.nft_token_id === id))
    .filter((n): n is ComposeNFT => !!n);

  if (loading) {
    return (
      <DefaultPageLayout>
        <div className="container max-w-6xl mx-auto px-4 py-12">
          <p className="text-body font-body text-subtext-color text-center">Loading Build Your Own...</p>
        </div>
      </DefaultPageLayout>
    );
  }

  if (error || !launch) {
    return (
      <DefaultPageLayout>
        <div className="container max-w-6xl mx-auto px-4 py-12 text-center">
          <p className="text-body font-body text-error-600 mb-4">{error || 'Launch not found'}</p>
          <Link to="/drops" className="text-brand-600 hover:underline">Browse Drops</Link>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="container max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            to={`/collections/${slug}`}
            className="flex items-center gap-2 text-caption font-caption text-subtext-color hover:text-default-font mb-3"
          >
            <FeatherArrowLeft className="w-4 h-4" />
            Back to Collection
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="text-heading-2 font-heading-2 text-default-font">
              Build Your Own
            </h1>
            <Badge variant="warning">{launch.name}</Badge>
          </div>
          <p className="text-body font-body text-subtext-color mt-1">
            Select trait NFTs to burn and combine into a new unique NFT.
          </p>
        </div>

        {/* Stats Bar */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <div className="p-3 bg-neutral-100 rounded-lg">
              <div className="text-caption font-caption text-subtext-color">Total Built</div>
              <div className="text-heading-3 font-heading-3 text-default-font">{stats.total_composed}</div>
            </div>
            <div className="p-3 bg-neutral-100 rounded-lg">
              <div className="text-caption font-caption text-subtext-color">Total Burned</div>
              <div className="text-heading-3 font-heading-3 text-default-font">{stats.total_burned}</div>
            </div>
            <div className="p-3 bg-neutral-100 rounded-lg">
              <div className="text-caption font-caption text-subtext-color">Unique Builders</div>
              <div className="text-heading-3 font-heading-3 text-default-font">{stats.unique_composers}</div>
            </div>
            <div className="p-3 bg-neutral-100 rounded-lg">
              <div className="text-caption font-caption text-subtext-color">Build Fee</div>
              <div className="text-heading-3 font-heading-3 text-default-font">
                {stats.composition_fee_per_nft_xrp} XRP/NFT
              </div>
            </div>
          </div>
        )}

        {/* Resume Banner for incomplete sessions */}
        {incompleteSession && isAuthenticated && (
          <div className="mb-6 p-4 border border-brand-200 bg-brand-50 rounded-lg flex items-center justify-between">
            <div>
              <div className="text-body-bold font-body-bold text-default-font">
                You have an unfinished build
              </div>
              <div className="text-caption font-caption text-subtext-color mt-0.5">
                {incompleteSession.burns_completed.length} of{' '}
                {incompleteSession.input_nft_token_ids.length} NFTs burned
                {' '}&middot; Status: {incompleteSession.status.replace(/_/g, ' ')}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="neutral-tertiary"
                size="medium"
                icon={<FeatherX />}
                onClick={handleCancelSession}
                loading={cancellingSession}
              >
                Cancel Build
              </Button>
              <Button
                variant="brand-primary"
                size="medium"
                icon={<FeatherZap />}
                onClick={() => setShowComposeModal(true)}
              >
                Resume Building
              </Button>
            </div>
          </div>
        )}

        {/* Main Content: Two Panel Layout */}
        {isAuthenticated ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Panel: NFT Selection Grid */}
            <div className="lg:col-span-2 space-y-6">
              {myNfts.length === 0 ? (
                <div className="text-center py-12 bg-neutral-100 rounded-lg">
                  <p className="text-body font-body text-subtext-color">
                    You don't own any NFTs from this collection yet.
                  </p>
                  <Link
                    to={`/drops/${slug}`}
                    className="text-brand-600 hover:underline text-body font-body mt-2 inline-block"
                  >
                    Mint some NFTs to get started
                  </Link>
                </div>
              ) : (
                <>
                  {/* Selected NFTs Section — always visible to prevent layout shift */}
                  <div>
                    <h2 className="text-body-bold font-body-bold text-default-font mb-3">
                      Selected to Combine ({selectedNfts.length})
                    </h2>
                    {selectedNfts.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {selectedNfts.map((nft) => (
                          <button
                            key={nft.nft_token_id}
                            type="button"
                            onClick={() => toggleNft(nft.nft_token_id)}
                            className="relative rounded-lg overflow-hidden border-2 border-brand-600 ring-2 ring-brand-200 transition-all"
                          >
                            {nft.image_url ? (
                              is3DModelUrl(nft.image_url) ? (
                                <div className="w-full aspect-square flex items-center justify-center bg-neutral-100">
                                  <div className="flex flex-col items-center gap-1">
                                    <span style={{ fontSize: '24px' }}>🧊</span>
                                    <span className="text-caption font-caption text-subtext-color">3D</span>
                                  </div>
                                </div>
                              ) : (
                                <img
                                  src={nft.image_url}
                                  alt={nft.name || nft.nft_token_id}
                                  className="w-full aspect-square object-cover"
                                />
                              )
                            ) : (
                              <div className="w-full aspect-square flex items-center justify-center bg-neutral-100">
                                <span className="text-caption font-caption text-subtext-color">No Image</span>
                              </div>
                            )}
                            <div className="p-2">
                              <div className="flex items-center gap-1">
                                <span className="text-caption font-caption text-default-font truncate flex-1">
                                  {nft.name || 'Unnamed'}
                                </span>
                                <Link
                                  to={`/nfts/${nft.nft_token_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-subtext-color hover:text-brand-600 flex-shrink-0"
                                >
                                  <FeatherExternalLink className="w-3 h-3" />
                                </Link>
                              </div>
                              <span className="text-caption font-caption text-subtext-color">
                                {nft.trait_count} {nft.trait_count === 1 ? 'trait' : 'traits'}
                              </span>
                            </div>
                            <div className="absolute top-2 right-2 w-5 h-5 bg-brand-600 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs font-bold">
                                {Array.from(selectedIds).indexOf(nft.nft_token_id) + 1}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        <div className="rounded-lg border-2 border-dashed border-neutral-border flex items-center justify-center aspect-square">
                          <p className="text-caption font-caption text-subtext-color text-center px-3">
                            Tap an NFT below to select it
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Available NFTs Section */}
                  {myNfts.filter((n) => !selectedIds.has(n.nft_token_id)).length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h2 className="text-body-bold font-body-bold text-default-font">
                          {selectedNfts.length > 0 ? 'Available NFTs' : 'Your NFTs'} ({myNfts.length - selectedNfts.length})
                        </h2>
                        <div className="relative">
                          <Button
                            variant="neutral-secondary"
                            size="small"
                            iconRight={<FeatherChevronDown />}
                            onClick={() => setShowSortDropdown(!showSortDropdown)}
                          >
                            {composeSortOptions.find((o) => o.value === sortOption)?.label}
                          </Button>
                          {showSortDropdown && (
                            <div className="absolute top-full right-0 mt-2 w-56 rounded-md border border-solid border-neutral-border bg-default-background shadow-lg z-10">
                              {composeSortOptions.map((option) => (
                                <button
                                  key={option.value}
                                  onClick={() => {
                                    setSortOption(option.value);
                                    setShowSortDropdown(false);
                                  }}
                                  className={`w-full text-left px-4 py-2 text-body font-body hover:bg-neutral-100 transition-colors ${
                                    sortOption === option.value ? 'bg-brand-100 text-brand-700' : 'text-default-font'
                                  }`}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {sortComposeNfts(
                          myNfts.filter((n) => !selectedIds.has(n.nft_token_id)),
                          sortOption
                        ).map((nft) => (
                            <button
                              key={nft.nft_token_id}
                              type="button"
                              onClick={() => toggleNft(nft.nft_token_id)}
                              className="relative rounded-lg overflow-hidden border-2 border-neutral-border hover:border-neutral-400 transition-all"
                            >
                              {nft.image_url && (
                                is3DModelUrl(nft.image_url) ? (
                                  <div className="w-full aspect-square flex items-center justify-center bg-neutral-100">
                                    <div className="flex flex-col items-center gap-1">
                                      <span style={{ fontSize: '24px' }}>🧊</span>
                                      <span className="text-caption font-caption text-subtext-color">3D</span>
                                    </div>
                                  </div>
                                ) : (
                                  <img
                                    src={nft.image_url}
                                    alt={nft.name || nft.nft_token_id}
                                    className="w-full aspect-square object-cover"
                                  />
                                )
                              )}
                              <div className="p-2">
                                <div className="flex items-center gap-1">
                                  <span className="text-caption font-caption text-default-font truncate flex-1">
                                    {nft.name || 'Unnamed'}
                                  </span>
                                  <Link
                                    to={`/nfts/${nft.nft_token_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-subtext-color hover:text-brand-600 flex-shrink-0"
                                  >
                                    <FeatherExternalLink className="w-3 h-3" />
                                  </Link>
                                </div>
                                <span className="text-caption font-caption text-subtext-color">
                                  {nft.trait_count} {nft.trait_count === 1 ? 'trait' : 'traits'}
                                </span>
                              </div>
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Right Panel: Preview & Compose Button */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-4">
                <h2 className="text-body-bold font-body-bold text-default-font">
                  Build Preview
                </h2>

                {/* Preview Canvas */}
                {launch.base_image_url && selectedNfts.length > 0 ? (
                  <ComposePreviewCanvas
                    baseImageUrl={launch.base_image_url}
                    selectedNfts={selectedNfts}
                    baseImageZIndex={launch.base_image_z_index ?? 0}
                  />
                ) : (
                  <div className="aspect-square bg-neutral-100 rounded-lg flex items-center justify-center">
                    <p className="text-caption font-caption text-subtext-color text-center px-4">
                      Select 2+ trait NFTs to preview your build
                    </p>
                  </div>
                )}

                {/* Validation Status */}
                {validation && (
                  <div className={`p-3 rounded-lg ${validation.valid ? 'bg-success-50 border border-success-200' : 'bg-error-50 border border-error-200'}`}>
                    {validation.valid ? (
                      <div className="text-caption font-caption text-success-700">
                        Valid combination! {validation.trait_count} traits
                      </div>
                    ) : (
                      <div className="text-caption font-caption text-error-700">
                        {validation.errors.map((e, i) => (
                          <div key={i}>{e}</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Combined Traits */}
                {validation?.valid && validation.combined_traits.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-caption font-caption text-subtext-color">Combined Traits:</div>
                    {validation.combined_traits.map((t) => {
                      const overwrite = validation.overwritten_traits?.find((o) => o.category === t.category);
                      return (
                        <div key={t.category} className="flex justify-between text-caption font-caption">
                          <span className="text-subtext-color">{t.category}</span>
                          <span className="text-default-font">
                            {overwrite && (
                              <span className="text-subtext-color line-through mr-1.5">{overwrite.original_value}</span>
                            )}
                            {t.value}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Fee & Compose Button */}
                {selectedIds.size >= 2 && (
                  <div className="space-y-3 pt-3 border-t border-neutral-border">
                    <div className="flex justify-between text-body font-body">
                      <span className="text-subtext-color">Build Fee</span>
                      <span className="text-default-font font-bold">
                        {validation ? `${validation.composition_fee_xrp} XRP` : '...'}
                      </span>
                    </div>
                    <div className="flex justify-between text-body font-body">
                      <span className="text-subtext-color">NFTs to Burn</span>
                      <span className="text-default-font font-bold">{selectedIds.size}</span>
                    </div>

                    <Button
                      variant="brand-primary"
                      size="large"
                      className="w-full"
                      disabled={!validation?.valid || validating}
                      onClick={() => setShowComposeModal(true)}
                      icon={<FeatherZap />}
                    >
                      Start Building
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 bg-neutral-100 rounded-lg">
            <p className="text-body font-body text-subtext-color mb-2">
              Connect your wallet to use Build Your Own
            </p>
          </div>
        )}

        {/* Recent Compose History */}
        {history.length > 0 && (
          <div className="mt-12">
            <h2 className="text-body-bold font-body-bold text-default-font mb-4">
              Recent Builds
            </h2>
            <div className="space-y-3">
              {history.map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-center gap-4 p-3 border border-neutral-border rounded-lg"
                >
                  {entry.result_image_url && (
                    <img
                      src={entry.result_image_url}
                      alt="Built NFT"
                      className="w-12 h-12 rounded object-cover"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-caption font-caption text-default-font">
                      {entry.burned_trait_count} NFTs burned &rarr; {entry.result_trait_count} trait build
                    </div>
                    <div className="text-caption font-caption text-subtext-color">
                      {entry.user_address.slice(0, 8)}...{entry.user_address.slice(-6)} &middot;{' '}
                      {new Date(entry.composed_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="text-caption font-caption text-subtext-color">
                    {entry.composition_fee_drops / 1_000_000} XRP
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Compose Progress Modal */}
      {showComposeModal && launch && (validation?.valid || incompleteSession) && (
        <ComposeProgressModal
          launchId={launch.id}
          nftTokenIds={Array.from(selectedIds)}
          compositionFeeDrops={validation?.composition_fee_drops ?? 0}
          onComplete={handleComposeComplete}
          onClose={() => setShowComposeModal(false)}
        />
      )}
    </DefaultPageLayout>
  );
};
